/**
 * v2.44 — Payment application API.
 *
 * CSRF pattern mirrors admin.ts: read the `pacebi_csrf` cookie and echo it
 * back via `X-CSRF-Token` on every state-changing POST.
 */
import type { ApplyPaymentRequest, ApplyPaymentResult, CashCode, FinalizeResult } from './types';

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function fetchCashCodes(sid: number): Promise<CashCode[]> {
  const res = await fetch(`/api/cash-codes?sid=${sid}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`cash-codes failed: ${res.status}`);
  return (await res.json()) as CashCode[];
}

/**
 * Apply a payment. Generates a fresh `idempotency_key` via `crypto.randomUUID()`
 * so retries from the same panel render won't double-post.
 */
export function applyPayment(req: Omit<ApplyPaymentRequest, 'idempotency_key'>): Promise<ApplyPaymentResult> {
  return postJSON<ApplyPaymentResult>('/api/payments/apply', {
    ...req,
    idempotency_key: crypto.randomUUID(),
  });
}

export function finalizePayment(params: { sid: number; tnum: number }): Promise<FinalizeResult> {
  return postJSON<FinalizeResult>('/api/payments/finalize', params);
}

export function voidPayment(params: { sid: number; payment_trankey: number }): Promise<{ voided: boolean }> {
  return postJSON<{ voided: boolean }>('/api/payments/void', params);
}
